import type { ReactElement } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { OperationBadge } from './OperationBadge';

export { UserFilterOption } from '../DataTableAlertsTab/FilterComponents';

function SelectionSlot({ isSelected }: { isSelected: boolean }): ReactElement {
  return (
    <div className="flex items-center justify-center w-4 h-4 shrink-0">
      {isSelected && (
        <Check className="size-4 text-primary" strokeWidth={2.5} />
      )}
    </div>
  );
}

/** Operation dropdown option — the same colored badge the cell renders. */
export function OperationFilterOption({
  operation,
  isSelected,
}: {
  operation: string;
  isSelected: boolean;
}): ReactElement {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <SelectionSlot isSelected={isSelected} />
      <OperationBadge operation={operation} />
    </div>
  );
}

/** Object-type dropdown option — the raw table name, matching the cell. */
export function ObjectTypeFilterOption({
  label,
  isSelected,
}: {
  label: string;
  isSelected: boolean;
}): ReactElement {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <SelectionSlot isSelected={isSelected} />
      <span className="text-sm truncate">{label}</span>
    </div>
  );
}

/**
 * Changes dropdown option — raw object table as a badge, then the raw field
 * name, matching the Object Type column and the changes cell. Uses the shared
 * selection slot so the check placement is consistent with the other filters.
 */
export function ChangesFilterOption({
  field,
  table,
  isSelected,
}: {
  field: string;
  table: string;
  isSelected: boolean;
}): ReactElement {
  return (
    <div className="flex items-center gap-1.5 flex-1 px-0.5 py-0.5 min-w-0">
      <SelectionSlot isSelected={isSelected} />
      <Badge
        variant="outline"
        className="shrink-0 border-blue-200 bg-blue-100 px-1.5 py-0 text-[10px] font-normal text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
      >
        {table}
      </Badge>
      <ChevronRight className="size-3 shrink-0 text-muted-foreground/60" />
      <span className="min-w-0 text-xs font-normal break-words">{field}</span>
    </div>
  );
}
